import React, { useContext, useRef, useState } from 'react'
import { BULK_DELETE, CREATE_UPDATE, DELETE_SELECTED, DOWNLOAD_ALL_AS_XLS, DOWNLOAD_TEMPLATE, MAX_RECATEGORIZE_PRODUCTS, PDW_CREATE, PDW_UPDATE, PDW_VALIDATE, RECATEGORIZE, RECATEGORIZE_LIMIT_TOOLTIP, UPLOAD_XLS, VIEW_LOGS } from '../ProductConstants'
import { TChildSelected, TProductSelected } from 'types/Pages/ProductDelta';
import { checkJobStatus } from 'api/Job';
import { downloadReversePDW } from 'api/Pdw';
import { AppContext } from 'Context/AppContext';
import { ProductsContext } from '../ProductsContext';
import { TFilters } from 'types/hooks/useTable';
import { MenuSection } from 'types/DesignSystem/Menu';
import Button from 'Components/DesignSystem/Button/Button';
import Menu from 'Components/DesignSystem/Menu/Menu';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import { useDismissable } from 'Components/DesignSystem/useDismissable';

type TDialogProps = {
  filters: TFilters,
  productsSelected: TProductSelected
  childSelected: TChildSelected
  openDeleteModal: () => void
  onOpenCreateModal: () => void;
  onOpenUpdateModal: () => void;
  onOpenValidateModal: () => void;
  onOpenRecategorizeModal: () => void;
  onOpenLogsModal: () => void;
  onOpenTemplateModal: () => void;
  downloadTemplate: () => void;
  openBulkDeleteXLSModal: () => void;
}

/**
 * The Bulk Action menu, on the DS toolbar grammar since § 65: a `subtle` trigger
 * and a sectioned `Menu` in place of hand-rolled buttons. Two deliberate behavior
 * changes from the legacy markup, both documented in okf/products/e2e.md § 4.2:
 *
 * - **"Delete Selected" and "Recategorize" are REALLY disabled when nothing is
 *   selected.** The old `tw-pointer-events-none` only stopped the mouse — a
 *   keyboard user could Tab in and Enter, opening the delete modal with zero
 *   products selected.
 * - Permission "denials" stay the old shape: the item looks enabled, clicking it
 *   raises an info toast and leaves the menu open (Recategorize alone is
 *   permission-disabled, as before).
 */
export const BulkAction = ({ filters, productsSelected, childSelected, openDeleteModal, onOpenCreateModal, onOpenUpdateModal, onOpenValidateModal, onOpenRecategorizeModal, onOpenLogsModal, onOpenTemplateModal, downloadTemplate, openBulkDeleteXLSModal }: TDialogProps) => {
  const [showMenu, setShowMenu] = useState<boolean>(false);
  const [downloadUrl, setDownloadUrl] = useState<string | undefined>(undefined)
  const appData = useContext(AppContext)
  const industryid = appData?.selectedIndustry && appData.selectedIndustry.id;
  const channelKey = appData?.channelKey;
  const prodContext = useContext(ProductsContext);
  const createPermission = appData.pm.checkPermission("gmsproducts_create");
  const updatePermission = appData.pm.checkPermission("gmsproducts_update");
  const deletePermission = appData.pm.checkPermission("gmsproducts_delete");
  const recategorizePermission = appData.pm.checkPermission("gmsproducts_recategorize");

  /*
    The wrapper (trigger + menu) is the dismiss boundary, and the hook is applied
    HERE rather than through Menu's `onDismiss`: the trigger sits outside the menu
    element, so Menu-level dismissal would fire on the trigger's own mousedown and
    the subsequent click would reopen — a menu that can never be toggled shut.
  */
  const wrapperRef = useRef<HTMLDivElement>(null);
  useDismissable({
    isOpen: showMenu,
    onDismiss: () => setShowMenu(false),
    isInside: (target) => !!wrapperRef.current?.contains(target),
  });

  /**
   * get the XLS using the current filters
   */
  async function downloadPDW() {
    const attributeString = filters?.filters?.join(',') ?? ""
    setDownloadUrl(undefined);
    if (!channelKey) return
    const result = await downloadReversePDW(industryid, channelKey, prodContext.selected_Category.id as string, attributeString)

    toast.info("Making request");
    if (!result[0]) return

    checkJobStatus(result[1]?.jobid as string)
      .then((jobdata: any) => {
        setDownloadUrl(jobdata.data)
        setTimeout(() => {
          setDownloadUrl(undefined);
        }, 4000)
      })
      .catch((err) => {
        console.error(err)
      })
  }

  /** Permitted → run and close; denied → toast and LEAVE OPEN (the legacy shape). */
  const gated = (permitted: boolean, action: () => void, deniedMessage: string) => () => {
    if (permitted) {
      action();
      setShowMenu(false);
    } else {
      toast.info(deniedMessage);
    }
  };

  const nothingSelected = productsSelected.length === 0 && childSelected.length === 0;
  const recategorizeExceedsLimit = productsSelected.length > MAX_RECATEGORIZE_PRODUCTS
  const recategorizeDisabled = !recategorizePermission || recategorizeExceedsLimit || nothingSelected

  const sections: MenuSection[] = [
    {
      label: CREATE_UPDATE,
      items: [
        { id: 'pdw-create', label: PDW_CREATE, 'data-testid': 'products-bulk-pdw-create-btn', onClick: gated(createPermission, onOpenCreateModal, "You don't have permission to Create the products.") },
        { id: 'pdw-update', label: PDW_UPDATE, 'data-testid': 'products-bulk-pdw-update-btn', onClick: gated(updatePermission, onOpenUpdateModal, "You don't have permission to Update the Products.") },
        { id: 'pdw-validate', label: PDW_VALIDATE, 'data-testid': 'products-bulk-pdw-validate-btn', onClick: gated(updatePermission, onOpenValidateModal, "You don't have permission to Update the products") },
        {
          id: 'recategorize',
          label: RECATEGORIZE,
          'data-testid': 'products-bulk-recategorize-btn',
          disabled: recategorizeDisabled,
          tooltip: recategorizeExceedsLimit ? RECATEGORIZE_LIMIT_TOOLTIP : undefined,
          onClick: gated(recategorizePermission, onOpenRecategorizeModal, "You don't have permission to Recategorize the products"),
        },
        { id: 'download-xls', label: DOWNLOAD_ALL_AS_XLS, 'data-testid': 'productlist-bulkaction-button-1', onClick: () => { downloadPDW(); setShowMenu(false); } },
        { id: 'download-template', label: DOWNLOAD_TEMPLATE, 'data-testid': 'productlist-bulkaction-button-2', onClick: () => { onOpenTemplateModal(); setShowMenu(false); } },
        { id: 'view-logs', label: VIEW_LOGS, 'data-testid': 'productlist-bulkaction-button-3', onClick: () => { onOpenLogsModal(); setShowMenu(false); } },
      ],
    },
    {
      label: BULK_DELETE,
      items: [
        {
          id: 'delete-selected',
          label: DELETE_SELECTED,
          'data-testid': 'productlist-bulkaction-button-4',
          disabled: nothingSelected,
          onClick: gated(deletePermission, openDeleteModal, "You don't have permission to delete the product."),
        },
        { id: 'upload-xls', label: UPLOAD_XLS, 'data-testid': 'productlist-bulkaction-button-5', onClick: gated(updatePermission, openBulkDeleteXLSModal, "You don't have permission to Delete the products") },
        { id: 'download-delete-template', label: DOWNLOAD_TEMPLATE, 'data-testid': 'productlist-bulkaction-button-6', onClick: () => { downloadTemplate(); setShowMenu(false); } },
      ],
    },
  ];

  return (
    <div ref={wrapperRef} className="tw-relative">
      <Button
        variant="subtle"
        id="products-bulk-action-btn"
        data-testid="products-bulk-action-btn"
        aria-haspopup="menu"
        aria-expanded={showMenu}
        onClick={() => setShowMenu((prev) => !prev)}
      >
        Bulk Action
      </Button>
      {showMenu && (
        <Menu
          sections={sections}
          /* compact: the legacy 28px-row density the reviewer preferred (§ 65). */
          variant="compact"
          className="tw-absolute tw-right-0 tw-top-full tw-mt-1 tw-w-48 tw-z-10 tw-shadow-ds-md"
          data-testid="products-bulk-action-menu"
        />
      )}
      <iframe
        style={{ display: "none" }}
        src={downloadUrl}
        id="iframe_pdw_download"
        title="pdw_download_create"
      />
    </div>
  )
}
